/* cspell:disable */
"use client";

import { useMemo, useState } from "react";
import styles from "./settlementCalculator.module.scss";

const translations = {
    en: {
        title: "How Much Is Your Case Worth? Try Our Calculator",
        subtitle:
            "Get an instant, estimated range of your potential settlement. This is the fastest way to gauge the financial impact of your accident based on your unique situation.",
        medicalExpenses: "Medical Expenses ($)",
        medicalTooltip: "Total medical bills resulting from the accident.",
        propertyDamage: "Property Damage ($)",
        propertyTooltip: "Cost to repair or replace your vehicle or property.",
        lostIncome: "Lost Income ($)",
        lostIncomeTooltip: "Wages you've already lost due to missing work.",
        futureMedical: "Future Medical ($)",
        futureMedicalTooltip:
            "Estimated future surgeries, therapy, or medications.",
        futureLostIncome: "Estimated Future Lost Income ($)",
        futureLostIncomeTooltip:
            "If your injury affects your ability to work in the future, enter the estimated loss",
        painSuffering: "Pain & Suffering (1–5)",
        painMinimal: "1 - Minimal",
        painMild: "2 - Mild",
        painModerate: "3 - Moderate",
        painSevere: "4 - Severe",
        painExtreme: "5 - Extreme",
        estimatedTotal: "ESTIMATED SETTLEMENT TOTAL",
        disclaimer:
            "This is a rough estimate of what your case may be worth and is not a guaranteed settlement amount. Only a qualified attorney can evaluate the full value of your claim.",
        resetCalculator: "Reset Calculator",
    },
    es: {
        title: "¿Cuánto valora su caso? Utilice nuestra calculadora",
        subtitle:
            "Obtenga al instante una estimación aproximada de la indemnización que podría recibir. Es la forma más rápida de evaluar las repercusiones económicas de su accidente teniendo en cuenta su situación particular.",
        medicalExpenses: "Gastos médicos ($)",
        medicalTooltip: "Total de facturas médicas resultantes del accidente.",
        propertyDamage: "Daños materiales ($)",
        propertyTooltip:
            "Costo de reparar o reemplazar su vehículo o propiedad.",
        lostIncome: "Pérdida de ingresos ($)",
        lostIncomeTooltip: "Salarios que ya ha perdido por faltar al trabajo.",
        futureMedical: "Gastos médicos futuros ($)",
        futureMedicalTooltip:
            "Cirugías, terapias o medicamentos futuros estimados.",
        futureLostIncome: "Estimación de pérdidas de ingresos futuros ($)",
        futureLostIncomeTooltip:
            "Si su lesión afecta su capacidad de trabajar en el futuro, ingrese la pérdida estimada",
        painSuffering: "Dolor y sufrimiento (1-5)",
        painMinimal: "1 - Mínimo",
        painMild: "2 - Menor",
        painModerate: "3 - Moderada",
        painSevere: "4 - Grave",
        painExtreme: "5 - Extrema",
        estimatedTotal: "ESTIMACIÓN DEL TOTAL DEL ACUERDO",
        disclaimer:
            "Esta es una estimación aproximada del valor de su caso y no supone una cantidad garantizada del acuerdo. Solamente un abogado calificado puede evaluar la totalidad del valor de su reclamo.",
        resetCalculator: "Reiniciar Calculadora",
    },
};

export default function SettlementCalculator({ locale }: { locale: string }) {
    const text =
        translations[locale as keyof typeof translations] || translations.en;

    const [medical, setMedical] = useState<number>(0);
    const [property, setProperty] = useState<number>(0);
    const [lostIncome, setLostIncome] = useState<number>(0);
    const [futureMedical, setFutureMedical] = useState<number>(0);
    const [futureLostIncome, setFutureLostIncome] = useState<number>(0);
    const [pain, setPain] = useState<number>(1);

    const total = useMemo(() => {
        const subtotal =
            medical + property + lostIncome + futureMedical + futureLostIncome;
        return subtotal * pain;
    }, [medical, property, lostIncome, futureMedical, futureLostIncome, pain]);

    const formatCurrency = (num: number) => {
        if (num > 1000000) return "> $1,000,000";
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD",
        }).format(num);
    };

    const resetCalculator = () => {
        setMedical(0);
        setProperty(0);
        setLostIncome(0);
        setFutureMedical(0);
        setFutureLostIncome(0);
        setPain(1);
    };

    return (
        <div className={styles.calcCard}>
            <div className={styles.calcHeader}>
                <h2>{text.title}</h2>
                <p>{text.subtitle}</p>
            </div>

            <div className={styles.inputGrid}>
                <InputField
                    label={text.medicalExpenses}
                    tooltip={text.medicalTooltip}
                    value={medical}
                    onChange={setMedical}
                />

                <InputField
                    label={text.propertyDamage}
                    tooltip={text.propertyTooltip}
                    value={property}
                    onChange={setProperty}
                />

                <InputField
                    label={text.lostIncome}
                    tooltip={text.lostIncomeTooltip}
                    value={lostIncome}
                    onChange={setLostIncome}
                />

                <InputField
                    label={text.futureMedical}
                    tooltip={text.futureMedicalTooltip}
                    value={futureMedical}
                    onChange={setFutureMedical}
                />

                <InputField
                    label={text.futureLostIncome}
                    tooltip={text.futureLostIncomeTooltip}
                    value={futureLostIncome}
                    onChange={setFutureLostIncome}
                />

                <div className={styles.fullWidth}>
                    <label>{text.painSuffering}</label>
                    <select
                        value={pain}
                        onChange={(e) => setPain(Number(e.target.value))}
                    >
                        <option value={1}>{text.painMinimal}</option>
                        <option value={2}>{text.painMild}</option>
                        <option value={3}>{text.painModerate}</option>
                        <option value={4}>{text.painSevere}</option>
                        <option value={5}>{text.painExtreme}</option>
                    </select>
                </div>
            </div>

            <div className={styles.resultArea}>
                <div
                    style={{
                        color: "#fff",
                        fontWeight: 700,
                        fontSize: 12,
                        letterSpacing: 1,
                    }}
                >
                    {text.estimatedTotal}
                </div>
                <div className={styles.totalVal}>{formatCurrency(total)}</div>

                <div className={styles.disclaimer}>{text.disclaimer}</div>
                <button
                    onClick={resetCalculator}
                    className={styles.resetCalculator}
                >
                    {text.resetCalculator}
                </button>
            </div>
        </div>
    );
}

type InputFieldProps = {
    label: string;
    tooltip: string;
    value: number;
    onChange: (value: number) => void;
};

function InputField({ label, tooltip, value, onChange }: InputFieldProps) {
    const [showTooltip, setShowTooltip] = useState(false);

    return (
        <div>
            <label>
                {label}
                <span
                    onMouseEnter={() => setShowTooltip(true)}
                    onMouseLeave={() => setShowTooltip(false)}
                    style={{
                        marginLeft: 6,
                        cursor: "help",
                        position: "relative",
                    }}
                >
                    ⓘ
                    {showTooltip && (
                        <span
                            style={{
                                position: "absolute",
                                bottom: "140%",
                                left: "50%",
                                transform: "translateX(-50%)",
                                width: 180,
                                background: "#333",
                                color: "#fff",
                                padding: 8,
                                borderRadius: 6,
                                fontSize: 10,
                                textAlign: "center",
                                zIndex: 10,
                            }}
                        >
                            {tooltip}
                        </span>
                    )}
                </span>
            </label>

            <input
                type="number"
                value={value || ""}
                onChange={(e) => onChange(Number(e.target.value) || 0)}
                placeholder="0.00"
            />
        </div>
    );
}
